import { Button, Loading } from '@wfp/react';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { receiveAPIResponse, requestAPI } from 'src/redux/data/actions';
import { apiCallAsync } from 'src/redux/sagas';
import { RootState } from 'src/redux/store';
import { pullPdf, stopPullingPdf } from 'src/redux/view/actions';
import {
  ACR_PDF_REPORT_FILE_PROXY_URL,
  GET_LIST_ACR_PDF_REPORTS_KEY,
  POST_REQUEST_ACR_PDF_REPORT_KEY,
  PULL_REQUESTED_PDF,
  REPORT_DETAIL_DATA_KEY,
} from 'src/redux/view/constants';
import { sendGAEvent, showNotification } from 'src/utils';
import { getActionAPISource } from 'src/utils';

const PDFGeneratorButton = ({ isPreview }: { isPreview: boolean }) => {
  const dispatch = useDispatch();
  const [notifiedPdfUuid, setNotifiedPdfUuid] = React.useState<string | null>(
    null,
  );
  const [isGeneratingPdf, setIsGeneratingPdf] = React.useState<boolean>(false);

  const viewState = useSelector((state: RootState) => state.viewState);
  const reportDetailData = useSelector(
    (state: RootState) => state.viewData[REPORT_DETAIL_DATA_KEY]?.data,
  );
  const reportVersions = useSelector(
    (state: RootState) => state.viewData[GET_LIST_ACR_PDF_REPORTS_KEY]?.data,
  );
  const pdfRequestResponse = useSelector(
    (state: RootState) => state.viewData[POST_REQUEST_ACR_PDF_REPORT_KEY],
  );
  const isPullingPdf = viewState?.[PULL_REQUESTED_PDF];

  // Stop pulling PDF when component will unmounted
  useEffect(() => {
    return () => {
      stopPullingPDF();
      resetPDFReportsList();
    };
  }, []);

  useEffect(() => {
    if (
      !isPullingPdf &&
      reportVersions?.report?.uuid &&
      reportVersions.report.is_generated &&
      reportVersions.report.uuid !== notifiedPdfUuid
    ) {
      getPDF(reportVersions.report.uuid);
    }
  }, [isPullingPdf, reportVersions, notifiedPdfUuid]);

  // Start pulling PDF when the API request succeeds
  useEffect(() => {
    if (pdfRequestResponse?.data && !isPullingPdf) {
      startPullingPDF();
      dispatch(
        receiveAPIResponse(
          POST_REQUEST_ACR_PDF_REPORT_KEY,
          {},
          {
            url_format: {
              _id_: reportDetailData?.pk,
            },
          },
        ),
      );
    }
  }, [pdfRequestResponse]);

  // Show error notification when the API request fails
  useEffect(() => {
    if (pdfRequestResponse?.error) {
      setIsGeneratingPdf(false);
      showNotification(
        'Failed to generate PDF report. Please try again.',
        'error',
        {
          autoClose: 5000,
        },
      );
      dispatch(
        receiveAPIResponse(
          POST_REQUEST_ACR_PDF_REPORT_KEY,
          {},
          {
            url_format: {
              _id_: reportDetailData?.pk,
            },
          },
        ),
      );
    }
  }, [pdfRequestResponse?.error]);

  function resetPDFReportsList() {
    dispatch(
      receiveAPIResponse(
        GET_LIST_ACR_PDF_REPORTS_KEY,
        {},
        {
          url_format: {
            _id_: reportDetailData.pk,
          },
        },
      ),
    );
  }

  function stopPullingPDF() {
    if (isPullingPdf) {
      dispatch(stopPullingPdf());
    }
  }

  function startPullingPDF() {
    if (!isPullingPdf && reportDetailData) {
      dispatch(
        pullPdf(
          requestAPI(GET_LIST_ACR_PDF_REPORTS_KEY, {
            url_format: { _id_: reportDetailData.pk },
          }),
        ),
      );
    }
  }

  function requestPDF() {
    if (!reportDetailData) return;

    setIsGeneratingPdf(true);
    setNotifiedPdfUuid(null);
    resetPDFReportsList();

    dispatch(
      requestAPI(POST_REQUEST_ACR_PDF_REPORT_KEY, {
        data: {},
        url_format: { _id_: reportDetailData.pk },
        method: 'post',
      }),
    );
  }

  async function getPDF(uuid: string) {
    const requestUrl = getActionAPISource(ACR_PDF_REPORT_FILE_PROXY_URL);
    const url = requestUrl.replace(new RegExp('_uuid_', 'g'), uuid);

    apiCallAsync(url)
      .then(result => {
        showNotification(
          <>
            PDF preview is ready.
            <br />
            <a
              className="open-pdf-link"
              href={result.data.s3_link}
              target="_blank"
              rel="noreferrer"
            >
              Open PDF
            </a>
          </>,
          'success',
          {
            autoClose: false,
            closeOnClick: false,
          },
        );
        setNotifiedPdfUuid(uuid);
        setIsGeneratingPdf(false);
        resetPDFReportsList();
      })
      .catch((err: Error) => {
        console.log(err);
        setIsGeneratingPdf(false);
        resetPDFReportsList();
      });
  }

  return (
    <Button
      className="wfp--btn--secondary"
      data-test-id="PDF-preview-button"
      disabled={isGeneratingPdf}
      onClick={() => {
        sendGAEvent('pdf_preview_btn_clicked');
        requestPDF();
      }}
    >
      {isGeneratingPdf ? (
        <>
          <Loading className="wfp--loading--small" withOverlay={false} active />
          Preparing PDF ...
        </>
      ) : (
        <>PDF {isPreview ? 'Preview' : 'Report'}</>
      )}
    </Button>
  );
};

export default PDFGeneratorButton;
